import {Component, OnInit} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {NgIf, NgForOf} from "@angular/common";
import * as XLSX from "xlsx";
import {Project} from "../../interfaces/project";
import {ProjectService} from "../../services/project.service";
import {SchoolLocations} from "../../data/locations";
import {AnnualSchoolProfileService} from "../../services/annual-school-profile.service";
import {AnnualSchoolProfile} from "../../interfaces/annual-school-profile";

@Component({
  selector: 'app-standard-form',
  standalone: true,
  imports: [FormsModule, NgIf, NgForOf],
  templateUrl: './standard-form.component.html',
  styleUrl: './standard-form.component.css'
})
export class StandardFormComponent implements OnInit {
  standardFormFlags = {showYear: false, showScope: false, showSelection: false};
  standardFormData = {
    selectedType: '', schoolYear: '', scope: '', selection: [] as string[] | string,
    selectionLabel: '', selectionOptions: [] as string[]
  };
  projects: Project[] = [];
  annualProfiles: AnnualSchoolProfile[] = [];

  constructor(private projectService: ProjectService, private annualService: AnnualSchoolProfileService) {
  }

  async ngOnInit() {
    this.projects = await this.projectService.getProjects();
    this.annualProfiles = await this.annualService.getProfiles()
  }

  onFormChange() {
    this.standardFormFlags = {showYear: false, showScope: false, showSelection: false};
    const data = this.standardFormData;

    data.schoolYear = '';
    data.scope = '';
    data.selection = '';
    data.selectionLabel = '';
    data.selectionOptions = [];

    if (data.selectedType !== null) {
      this.standardFormFlags.showYear = true;
    }
  }

  onScopeChange() {
    const data = this.standardFormData;
    const flags = this.standardFormFlags;
    flags.showSelection = false;

    const scope = data.scope;
    if (scope === 'school') {
      flags.showSelection = true;
      data.selectionLabel = 'Select School';
      data.selectionOptions = this.getSchools();
      data.selection = '';
    } else if (scope === 'cluster') {
      flags.showSelection = true;
      data.selectionLabel = 'Select Cluster';
      data.selectionOptions = this.getClusters();
      data.selection = '';
    } else if (scope === 'all') {
      flags.showSelection = false;
      data.selectionOptions = [];
      data.selection = this.getSchools();
    }
  }

  getAnnualSchoolProfile(school: string, year: string): AnnualSchoolProfile | undefined {
    const searchKey = `${school} ${year}`.trim().toLowerCase();

    return this.annualProfiles.find(p =>
      `${p.school} ${p.schoolYear}`.trim().toLowerCase() === searchKey
    );
  }

  getWatershedCategories(p: any): string[] {
    return [
      p.waterEducationalCampaign && 'Water Educational Campaign',
      p.adoptARoadWatershed && 'Adopt-A-Road',
      p.adoptAStreamCleanup && 'Adopt-A-Stream Clean-up',
      p.adoptAStreamMonitoring && 'Adopt-A-Stream Monitoring',
      p.streamRestoration && 'Stream Restoration/Erosion Control',
      p.greatDaysService && 'GC&B Great Days of Service',
      p.wetlandCleanup && 'GC&B Wetland Cleanup',
      p.yellowRiverTrashBash && 'Yellow River Trash Bash',
      p.shoreSweep && 'Shore Sweep (Lake Lanier)',
      p.localWaterEvent && 'Other local water-related event',
      p.gwinnettServiceDept && 'Other Gwinnett County Service Dept.',
      p.supportCharity && 'Support local charity or non-profit',
      p.wasteWaterPlant && 'Waste Water Plant',
      p.watersheddrinkingWaterPlant && 'Drinking Water Plant',
      p.theWaterTower && 'The Water Tower',
      p.deptOfWaterResources && 'Dept of Water Resources',
      p.gwinnettCleanBeautiful && 'Gwinnett Clean & Beautiful',
      p.elachee && 'Elachee Nature Center',
      p.watershedOtherCheck && 'Other'
    ].filter(Boolean);
  }

  hasFoodAudit(project: Project) {
    return project.fwasteaudit || !!project.foodAuditDate || !!project.foodAuditNumLunchesServed;
  }

  hasWatershed(p: any): boolean {
    return !!(
      p.waterEducationalCampaign ||
      p.adoptARoadWatershed ||
      p.adoptAStreamCleanup ||
      p.adoptAStreamMonitoring ||
      p.streamRestoration ||

      p.greatDaysService ||
      p.wetlandCleanup ||
      p.yellowRiverTrashBash ||
      p.shoreSweep ||
      p.localWaterEvent ||
      p.gwinnettServiceDept ||
      p.supportCharity ||

      p.wasteWaterPlant ||
      p.watersheddrinkingWaterPlant ||
      p.theWaterTower ||
      p.deptOfWaterResources ||
      p.gwinnettCleanBeautiful ||
      p.elachee ||
      p.watershedOtherCheck
    );
  }

  getSchools(): string[] {
    const year = this.standardFormData.schoolYear;
    const type = this.standardFormData.selectedType;

    if (!year || !type) return [];

    // Annual School Profile
    if (type === 'annual') {
      return this.annualProfiles
        .filter(p => year.includes(p.schoolYear))
        .map(p => p.school)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    // Food Audit
    if (type === 'food') {
      return this.projects
        .filter(p => this.hasFoodAudit(p) && p.schoolYear === year)
        .map(p => p.school)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    // Watershed
    if (type === 'watershedProject' || type === 'watershedSchool') {
      return this.projects
        .filter(p =>
          this.hasWatershed(p) &&
          this.isWithinReportingYear(p.endDate, year)
        )
        .map(p => p.school)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    // Projects
    return this.projects
      .filter(p => p.schoolYear === year)
      .map(p => p.school)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
  }

  getClusters(): string[] {
    const year = this.standardFormData.schoolYear;
    const type = this.standardFormData.selectedType;

    if (!year || !type) return [];

    // Annual School Profile
    if (type === 'annual') {
      const schools = this.annualProfiles
        .filter(p => p.schoolYear === year)
        .map(p => p.school);

      return SchoolLocations
        .filter(loc => schools.includes(loc.SchoolName))
        .map(loc => loc.Cluster)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    let schools: string[] = [];

    if (type === 'food') {
      schools = this.projects
        .filter(p => this.hasFoodAudit(p) && p.schoolYear === year)
        .map(p => p.school);
    }

    if (type === 'watershedProject' || type === 'watershedSchool') {
      schools = this.projects
        .filter(p =>
          this.hasWatershed(p) &&
          this.isWithinReportingYear(p.endDate, year)
        )
        .map(p => p.school);
    }

    if (type === 'project') {
      schools = this.projects
        .filter(p => p.schoolYear === year)
        .map(p => p.school);

      return SchoolLocations
        .filter(loc => schools.includes(loc.SchoolName))
        .map(loc => loc.Cluster)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    return SchoolLocations
      .filter(loc => schools.includes(loc.SchoolName))
      .map(loc => loc.Cluster)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
  }

  // YEARS
  onYearChange() {
    this.standardFormFlags.showScope = true;
    this.standardFormFlags.showSelection = false;
    this.standardFormData.scope = '';
    this.standardFormData.selection = '';
  }

  getYears(): string[] {
    if (this.standardFormData.selectedType === 'annual') {
      return this.annualProfiles
        .map(p => p.schoolYear)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    if (this.standardFormData.selectedType === 'food') {
      return this.projects
        .filter(p => this.hasFoodAudit(p))
        .map(p => p.schoolYear)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    }

    return this.projects
      .filter(p => this.hasWatershed(p) && p.endDate)
      .map(p => {
        const d = new Date(p.endDate);
        const y = d.getFullYear();
        const m = d.getMonth();
        return m >= 10 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
      })
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
  }

  isWithinReportingYear(endDate: string | Date, reportingYear: string | string[]): boolean {
    if (!endDate || !reportingYear) return false;

    const projectDate = new Date(endDate);

    const years = Array.isArray(reportingYear) ? reportingYear : [reportingYear];

    return years.some(yearStr => {
      const [startYearStr, endYearStr] = yearStr.split('-');
      const startYear = parseInt(startYearStr, 10);
      const endYear = parseInt(endYearStr, 10);

      const startDate = new Date(startYear, 10, 1); // Nov 1
      const endDateLimit = new Date(endYear, 9, 31, 23, 59, 59); // Oct 31

      return projectDate >= startDate && projectDate <= endDateLimit;
    });
  }

  // GENERATE
  canGenerate(): boolean {
    const data = this.standardFormData;
    if (!data.schoolYear || !data.scope) return false;
    if (data.scope === 'all') return Array.isArray(data.selection) && data.selection.length > 0;
    return !!data.selection;
  }

  generateExcelReport() {
    const data = this.standardFormData;

    if (data.selectedType === 'annual') {
      this.generateAnnualSchoolProfileReport();
      return;
    }

    if (data.selectedType === 'watershedProject' || data.selectedType === 'watershedSchool') {
      this.generateWaterReports();
      return;
    }

    if (!this.canGenerate()) {
      alert('Please select a form, year, and scope first.');
      return;
    }

    let filteredProjects = this.projects.filter(p => {
      if (data.selectedType === 'food') {
        return this.hasFoodAudit(p) && p.schoolYear === data.schoolYear;
      } else if (data.selectedType === 'project') {
        return p.schoolYear === data.schoolYear;
      }
      return false;
    });

    filteredProjects = filteredProjects.filter(
      (p, i, arr) => arr.findIndex(x => x.id === p.id) === i
    );

    if (data.scope === 'school') {
      filteredProjects = filteredProjects.filter(p => p.school === data.selection);
    }

    if (data.scope === 'cluster') {
      filteredProjects = filteredProjects.filter(p => {
        const cluster = SchoolLocations.find(
          loc => loc.SchoolName.trim().toLowerCase() === p.school.trim().toLowerCase()
        )?.Cluster;
        return cluster?.trim().toLowerCase() === (data.selection as string).trim().toLowerCase();
      });
    }

    if (data.scope === 'all') {
      filteredProjects = filteredProjects.filter(p =>
        (data.selection as string[]).includes(p.school)
      );
    }

    const excelData = filteredProjects.map(p => {
      const profile = this.getAnnualSchoolProfile(p.school, p.schoolYear);
      return {
        School: p.school,
        Cluster: SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster || '',
        SchoolYear: p.schoolYear,
        ProjectStatus: p.projectStatus,
        ProjectOwner: p.representativeName,
        isFirstAudit: p.fwaIsFirstAudit,
        ComparisonToFirst: p.fwaComparisonToPrevious,
        AttributionOfFoodWasteChange: p.fwaWasteAttribution,
        AuditDate: p.foodAuditDate || p.fwaAuditDate || '',
        UpdatedDate: p.dateEdited ? new Date(p.dateEdited).toISOString().split('T')[0] : '',
        StudentsServed: p.foodAuditNumLunchesServed || p.fwaStudentsServed || 0,
        MenuItems: p.foodAuditMenuItems || p.fwaMenuItems || '',
        RecoverableItems: p.fwaRecoverableItemsList || '',
        RecoverablesLbs: p.fwaRecoverables || 0,
        MilkLbs: p.fwaMilk || 0,
        OtherLiquidsLbs: p.fwaOtherLiquids || 0,
        FruitsVegetablesLbs: p.fwaFruitsVegetables || 0,
        MeatsGrainsOtherLbs: p.fwaMeatsGrainsOther || 0,
        TotalFoodWasteLbs: p.fwaTotalFoodWaste || 0,
        FoodWastePerStudentLbs: p.fwaFoodWastePerStudent || 0,
        TrashPackagingLbs: p.fwaTrashPackaging || 0,
        RecyclingLbs: p.fwaRecycling || 0,
        ParentHelpers: p.fwaParentHelpers || 0,
        StudentsParticipating: p.fwaStudentsParticipating || 0,
        CommunityMembers: p.fwaCommunityMembers || 0,
        StudentFeedback: Array.isArray(p.fwaStudentFeedback)
          ? p.fwaStudentFeedback.map(f => f.replace(/\s*\([^)]*\)/g, '').trim()).join('; ')
          : (p.fwaStudentFeedback || '').replace(/\s*\([^)]*\)/g, '').trim(),
        SolutionsImplemented: p.fwaSolutionsImplemented || '',
        GrantReceived: p.fwaGrantReceived ? 'Yes' : 'No',
        GrantValue: p.fwaGrantValue || 0,
        AnnualSchoolProfile: profile ? 'YES' : 'NO',
        ProjectId: p.id
      };
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Standard Report');
    XLSX.writeFile(wb, 'Standard_Report.xlsx');
  }

  generateWaterReports() {
    const data = this.standardFormData;
    const bySchool = data.selectedType === 'watershedSchool';

    let filteredProjects = this.projects.filter(p =>
      this.hasWatershed(p) &&
      this.isWithinReportingYear(p.endDate, data.schoolYear) &&
      (!bySchool || p.projectStatus === 'Completed')
    );

    filteredProjects = filteredProjects.filter(
      (p, i, arr) => arr.findIndex(x => x.id === p.id) === i
    );

    if (data.scope === 'school') {
      filteredProjects = filteredProjects.filter(p =>
        p.school.trim().toLowerCase() === (data.selection as string).trim().toLowerCase()
      );
    } else if (data.scope === 'cluster') {
      filteredProjects = filteredProjects.filter(p => {
        const cluster = SchoolLocations.find(loc => loc.SchoolName.trim().toLowerCase() === p.school.trim().toLowerCase())?.Cluster;
        return cluster?.trim().toLowerCase() === (data.selection as string).trim().toLowerCase();
      });
    } else if (data.scope === 'all') {
      filteredProjects = filteredProjects.filter(p => (data.selection as string[]).includes(p.school));
    }

    //by school
    if (bySchool) {
      const grouped: any = {};
      filteredProjects.forEach(p => {
        if (!grouped[p.school]) {
          grouped[p.school] = {SchoolName: p.school, Activities: [], Students: 0, Duration: 0, Hours: 0};
        }
        const categories = this.getWatershedCategories(p).join(', ');
        grouped[p.school].Activities.push(`${categories} ${p.endDate || ''}`);
        grouped[p.school].Students += Number(p.numStudentsInProject || 0);
        grouped[p.school].Duration += Number(p.eventDuration || 0);
        grouped[p.school].Hours += Number(p.numParticipationHours || 0);
      });

      const totalsRows = Object.values(grouped).map((g: any) => ({
        SchoolName: g.SchoolName,
        Activities: g.Activities.join('; '),
        StudentsInvolvedTotal: g.Students,
        EventDurationTotal: g.Duration,
        ParticipationHoursTotal: g.Hours
      }));

      const grandTotals = {Students: 0, Duration: 0, Hours: 0};
      totalsRows.forEach(r => {
        grandTotals.Students += r.StudentsInvolvedTotal;
        grandTotals.Duration += r.EventDurationTotal;
        grandTotals.Hours += r.ParticipationHoursTotal;
      });
      totalsRows.push({
        SchoolName: 'TOTALS',
        Activities: '',
        StudentsInvolvedTotal: grandTotals.Students,
        EventDurationTotal: grandTotals.Duration,
        ParticipationHoursTotal: grandTotals.Hours
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(totalsRows), 'Water Totals by School');
      XLSX.writeFile(wb, 'Water_Education_Reports.xlsx');
    } else {
      // by project
      const detailRows = filteredProjects.map(p => {
        const categories = this.getWatershedCategories(p);
        const projectCategories: string[] = [];
        if (p.waterEducationalCampaign) projectCategories.push('Watershed Programs');
        if (p.greatDaysService || p.wetlandCleanup || p.yellowRiverTrashBash || p.shoreSweep ||
          p.localWaterEvent || p.gwinnettServiceDept || p.supportCharity) {
          projectCategories.push('Community Watershed Events');
        }
        if (p.wasteWaterPlant || p.watersheddrinkingWaterPlant || p.theWaterTower ||
          p.deptOfWaterResources || p.gwinnettCleanBeautiful || p.elachee) {
          projectCategories.push('Watershed Field Trips/In-House Programs');
        }
        return {
          'Project Status': p.projectStatus,
          'Project Name': p.title,
          'Project Start Date': p.startDate || '',
          'Project End Date': p.endDate || '',
          School: p.school,
          Cluster: SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster || '',
          'School Year': p.schoolYear,
          'Project Category': projectCategories.join('; '),
          'Project Subcategories': categories.join('; '),
          'Students Involved': p.numStudentsInProject || 0,
          'Event Duration': p.eventDuration || 0,
          'Participation Hours': p.numParticipationHours || 0,
          'Project Owner': p.representativeName,
          'Updated Date': p.dateEdited ? new Date(p.dateEdited).toISOString().split('T')[0] : '',
          'Record ID': p.id
        };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Water Projects Detail');
      XLSX.writeFile(wb, 'Water_Projects_Detail.xlsx');
    }
  }

  generateAnnualSchoolProfileReport() {
    const data = this.standardFormData;
    const years = Array.isArray(data.schoolYear) ? data.schoolYear : [data.schoolYear];
    let selectedSchools: string[] = [];

    if (data.scope === 'all') {
      selectedSchools = SchoolLocations.map(s => s.SchoolName);
    } else if (data.scope === 'cluster') {
      selectedSchools = SchoolLocations
        .filter(s => s.Cluster === data.selection)
        .map(s => s.SchoolName);
    } else if (data.scope === 'school') {
      selectedSchools = [data.selection as string];
    }

    // Map schools to profile if exists
    const filteredProfiles = selectedSchools.map(p =>
      this.annualProfiles.find(profile => profile.school === p && years.includes(profile.schoolYear))
    );

    const excelData = selectedSchools.map(schoolName => {
      const profile = this.annualProfiles.find(p => p.school === schoolName && years.includes(p.schoolYear));
      return {
        School: schoolName,
        Cluster: SchoolLocations.find(loc => loc.SchoolName === schoolName)?.Cluster || '',
        Year: profile?.schoolYear || years[0],
        'City Limits': profile?.isWithinCityLimits || '',
        'Teacher Rep Name': profile?.ghsRepresentativeName || '',
        'Teacher Rep Email': profile?.ghsRepresentativeEmail || '',
        'Number of Students Enrolled in School': profile?.numOfStudents || '',
        'Number of Teachers and Support Staff': profile?.numOfTeacherAndStaff || '',
        Goals: Array.isArray(profile?.goals) ? profile?.goals.join('; ') : profile?.goals || '',
        'Other goals commentary': profile?.goalsComment || '',
        'Created Date': profile?.submissionDate ? new Date(profile.submissionDate).toISOString().split('T')[0] : '',
        'Created By': profile?.createdBy || '',
        'Updated Date': profile?.editedDate ? new Date(profile.editedDate).toISOString().split('T')[0] : '',
        'Updated By': profile?.updatedBy || '',
        'Record ID': profile?.id || ''
      };
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Annual School Profile');
    XLSX.writeFile(wb, 'Annual_School_Profile.xlsx');
  }

  protected readonly Array = Array;
}
